/**
 * Lighting. Per docs/RENDERING.md this is the cheapest large win available -
 * tone mapping and sun angle cost nothing and change the image more than any
 * amount of extra geometry.
 *
 * Three decisions carry most of it:
 *
 *   ACES tone mapping + sRGB output. Without it every scene reads flat and
 *   "digital", and no amount of material work recovers that.
 *
 *   Mid-afternoon sun (~3-4 pm), low and off-axis. Long shadows give flat
 *   extruded geometry its only depth cue. A noon sun is the worst possible
 *   choice and is what most WebGL scenes default to.
 *
 *   A shadow camera that covers ~80 m around the car and nothing else.
 *   The backdrop does not cast or receive; at 200 m under fog nobody can tell,
 *   and the texel budget all goes where the player is looking.
 */
import {
  AmbientLight,
  Color,
  DirectionalLight,
  HemisphereLight,
  Object3D,
  Scene,
  Vector3,
  type WebGLRenderer,
} from 'three';
import { QUALITY_PROFILES, type Quality } from '../core/perf';

/** Shadow coverage radius around the car, metres. */
export const SHADOW_RADIUS = 40;

/**
 * v0.2 P0 defaults: single source of truth for easy A/B.
 * Benchmark proposal was exposure 1.15/1.22/1.28 and sun 3.8/4.2.
 * This commit picks the conservative centre (1.18 / 4.0) and keeps
 * ambient low so the 2:1 front:side ratio is preserved.
 */
export const LIGHTING_DEFAULTS = {
  sunIntensity: 4.0,
  hemiIntensity: 0.52,
  ambientIntensity: 0.08,
} as const;

function parseLightingOverride(query: URLSearchParams): {
  sunIntensity?: number;
  hemiIntensity?: number;
  ambientIntensity?: number;
} {
  const out: { sunIntensity?: number; hemiIntensity?: number; ambientIntensity?: number } = {};
  const sun = Number(query.get('sun'));
  if (Number.isFinite(sun) && sun >= 1 && sun <= 8) out.sunIntensity = sun;
  const hemi = Number(query.get('hemi'));
  if (Number.isFinite(hemi) && hemi >= 0 && hemi <= 1.5) out.hemiIntensity = hemi;
  const ambient = Number(query.get('ambient'));
  if (Number.isFinite(ambient) && ambient >= 0 && ambient <= 0.5) out.ambientIntensity = ambient;
  return out;
}

export interface Lighting {
  sun: DirectionalLight;
  readonly radiusM: number;
  /** Keep the shadow frustum centred on the car. */
  follow(x: number, z: number): void;
  setQuality(quality: Quality): void;
}

export function setupLighting(
  scene: Scene,
  renderer: WebGLRenderer,
  quality: Quality,
  query: URLSearchParams = new URLSearchParams(location.search),
): Lighting {
  // ~3:30 pm in Hong Kong: sun in the west, ~35 deg above the horizon.
  const azimuth = Math.PI * 0.72;
  const elevation = Math.PI * 0.19;
  const dir = new Vector3(
    Math.cos(elevation) * Math.sin(azimuth),
    Math.sin(elevation),
    Math.cos(elevation) * Math.cos(azimuth),
  );

  const overrides = parseLightingOverride(query);
  const sunIntensity = overrides.sunIntensity ?? LIGHTING_DEFAULTS.sunIntensity;
  const sun = new DirectionalLight(new Color(0xfff2dd), sunIntensity);
  sun.position.copy(dir).multiplyScalar(300);
  sun.castShadow = QUALITY_PROFILES[quality].shadows;

  const cam = sun.shadow.camera;
  cam.left = -SHADOW_RADIUS;
  cam.right = SHADOW_RADIUS;
  cam.top = SHADOW_RADIUS;
  cam.bottom = -SHADOW_RADIUS;
  cam.near = 200;
  cam.far = 400;
  cam.updateProjectionMatrix();

  const initialShadowSize = QUALITY_PROFILES[quality].shadowMapSize;
  sun.shadow.mapSize.set(initialShadowSize, initialShadowSize);
  // Normal bias handles the near-tangential sun on the road much better than a
  // constant bias, which is what causes shadow acne on a shallow-crossfall surface.
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.03;

  const target = new Object3D();
  scene.add(target);
  sun.target = target;
  scene.add(sun);

  // Sky/ground bounce. Hong Kong afternoons have a lot of it: bright haze
  // above, warm concrete below. Kept well below the sun's intensity - lifting
  // the fill until nothing is dark is the single fastest way to make a scene
  // read as flat and computer-generated, and it is what the reference shots of
  // the competing product look like.
  // v0.2 P0: sun ↑, hemi/ambient small ↑, AO preserved (roadAo + shadow).
  const hemi = new HemisphereLight(
    0xbcd4e8,
    0x6b6357,
    overrides.hemiIntensity ?? LIGHTING_DEFAULTS.hemiIntensity,
  );
  scene.add(hemi);
  // Just enough flat fill that shadow interiors do not crush to black under ACES.
  scene.add(new AmbientLight(0xffffff, overrides.ambientIntensity ?? LIGHTING_DEFAULTS.ambientIntensity));

  renderer.shadowMap.enabled = QUALITY_PROFILES[quality].shadows;

  return {
    sun,
    radiusM: SHADOW_RADIUS,
    follow(x: number, z: number) {
      target.position.set(x, 0, z);
      target.updateMatrixWorld();
      sun.position.set(x + dir.x * 300, dir.y * 300, z + dir.z * 300);
    },
    setQuality(nextQuality) {
      const profile = QUALITY_PROFILES[nextQuality];
      const sizeChanged = sun.shadow.mapSize.x !== profile.shadowMapSize;
      renderer.shadowMap.enabled = profile.shadows;
      sun.castShadow = profile.shadows;
      sun.shadow.mapSize.set(profile.shadowMapSize, profile.shadowMapSize);
      if (sizeChanged && sun.shadow.map) {
        // Shadow targets are allocated lazily. Releasing the old one is what
        // makes a downgrade return GPU memory instead of only changing a label.
        sun.shadow.map.dispose();
        sun.shadow.map = null;
      }
      sun.shadow.needsUpdate = true;
    },
  };
}
